import React from "react";
import classNames from "classnames";
import {
  CategoryIconMap,
  DestinationCategory,
} from "../../models/DestinationCategory";
import { getCategoryLabel, useTranslation } from "../../i18n/LanguageProvider";

interface CategorySelectorProps {
  selectedCategories: DestinationCategory[];
  setSelectedCategories: (categories: DestinationCategory[]) => void;
}

const allCategories = Object.values(DestinationCategory);

const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategories,
  setSelectedCategories,
}) => {
  const { t } = useTranslation();

  const toggleCategory = (value: DestinationCategory) => {
    if (selectedCategories.includes(value)) {
      setSelectedCategories(selectedCategories.filter((v) => v !== value));
    } else {
      setSelectedCategories([...selectedCategories, value]);
    }
  };

  const allSelected = allCategories.every((c) =>
    selectedCategories.includes(c)
  );

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedCategories([]); // Deselect all
    } else {
      setSelectedCategories(allCategories); // Select all
    }
  };

  return (
    <div className="selector-panel p-4 sm:p-6 bg-[#FBFBFF] shadow-card rounded-2xl w-full">
      <div className="flex flex-wrap gap-3 justify-between items-start mb-4">
        <h2 className="selector-heading text-accent font-semibold text-sm">{t("categorySelector.title")}</h2>
        <button
          onClick={handleToggleAll}
          className="text-sm text-[#A0A0A0] hover:underline"
        >
          {allSelected ? t("categorySelector.deselectAll") : t("categorySelector.selectAll")}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 max-w-2xl">
        {allCategories.map((category) => {
          const isSelected = selectedCategories.includes(category);
          return (
            <button
              key={category}
              aria-pressed={isSelected}
              onClick={() => toggleCategory(category)}
              className={classNames(
                "rounded-xl px-4 py-2 shadow-sm transition flex items-center hover:scale-105 gap-2 text-sm font-semibold transform",
                isSelected
                  ? "bg-[#4B4B4B] text-white hover:bg-[#3F3F3F]"
                  : "bg-[#EAEAEA] text-[#4B4B4B] hover:bg-[#D5D5D5]"
              )}
            >
              <span className="text-lg">{CategoryIconMap[category]}</span>
              <span>{getCategoryLabel(category, t)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CategorySelector;
